import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileUpload } from "@/components/FileUpload";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export const ResourceManager = ({ endpoint, title, fields, emptyItem, renderRow }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/${endpoint}`).then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [endpoint]);

  const openAdd = () => { setEditing(null); setForm(emptyItem); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyItem, ...item }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success("सफलतापूर्वक सहेजा गया");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("सहेजने में त्रुटि: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleteId}`);
      toast.success("हटा दिया गया");
      load();
    } catch { toast.error("हटाने में त्रुटि"); }
    setDeleteId(null);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-[#0a2540]">{title} <span className="text-base text-slate-400">({items.length})</span></h2>
        <Button onClick={openAdd} data-testid={`add-${endpoint}-button`} className="bg-[#0a2540] hover:bg-[#061729] gap-2">
          <Plus size={17} /> नया जोड़ें
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-amber-200/60">अभी तक कुछ नहीं जोड़ा गया है।</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`admin-row-${item.id}`} className="bg-white border border-amber-200/60 rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">{renderRow(item)}</div>
              <div className="flex gap-2 shrink-0">
                <Button size="icon" variant="outline" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`} className="border-amber-300 text-amber-700">
                  <Pencil size={16} />
                </Button>
                <Button size="icon" variant="outline" onClick={() => setDeleteId(item.id)} data-testid={`delete-${item.id}`} className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${endpoint}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif-deva text-xl">{editing ? "संपादित करें" : `नया ${title}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-[#0a2540]">{f.label}{f.required && " *"}</Label>
                <div className="mt-1.5">
                  {f.type === "textarea" ? (
                    <Textarea value={form[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} rows={f.rows || 4} className="border-amber-200" data-testid={`field-${f.key}`} />
                  ) : f.type === "select" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                      <SelectTrigger className="border-amber-200" data-testid={`field-${f.key}`}><SelectValue placeholder="चुनें" /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "image" ? (
                    <FileUpload value={form[f.key]} onChange={(v) => setField(f.key, v)} kind="image" accept="image/*" testId={`upload-${f.key}`} />
                  ) : f.type === "pdf" ? (
                    <FileUpload value={form[f.key]} onChange={(v) => setField(f.key, v)} kind="pdf" accept="application/pdf" testId={`upload-${f.key}`} />
                  ) : (
                    <Input value={form[f.key] || ""} onChange={(e) => setField(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} type={f.type === "number" ? "number" : "text"} className="border-amber-200" data-testid={`field-${f.key}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>रद्द करें</Button>
            <Button onClick={save} disabled={saving} data-testid={`save-${endpoint}-button`} className="bg-[#0a2540] hover:bg-[#061729] gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />} सहेजें
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>क्या आप निश्चित हैं?</AlertDialogTitle>
            <AlertDialogDescription>यह आइटम स्थायी रूप से हटा दिया जाएगा।</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>रद्द करें</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-button" className="bg-red-600 hover:bg-red-700">हटाएं</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const RowThumb = ({ url, kind = "image", fallback }) => (
  url ? (
    kind === "image" ? <img src={fileUrl(url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> :
    <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 shrink-0"><i className="fa-solid fa-file-pdf" /></div>
  ) : (
    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300 shrink-0"><i className={`fa-solid fa-${fallback || "image"}`} /></div>
  )
);
